import React, { useEffect, useState, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Mail, Send, Paperclip, ArrowLeft, RefreshCw, Search,
  Download, X, User, Inbox, Reply, ReplyAll, Forward, PenSquare,
  ChevronDown, ChevronUp
} from 'lucide-react';

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const getInitials = (str) => {
  if (!str) return '?';
  const clean = str.split('<')[0].replace(/"/g, '').trim();
  return clean.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
};

const getDisplayName = (str) => {
  if (!str) return 'Sconosciuto';
  return str.split('<')[0].replace(/"/g, '').trim() || str;
};

const extractEmail = (str) => {
  if (!str) return '';
  const match = str.match(/<(.+?)>/);
  return match ? match[1] : str.trim();
};

export const Emails = () => {
  const { user, isAdmin } = useAuth();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetail, setEmailDetail] = useState(null);
  const [replies, setReplies] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [users, setUsers] = useState([]);
  // Compose state
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState('new'); // new, reply, replyAll, forward
  const [composeTo, setComposeTo] = useState('');
  const [composeCc, setComposeCc] = useState('');
  const [composeBcc, setComposeBcc] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeFiles, setComposeFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [replyToId, setReplyToId] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const composeFileRef = useRef(null);

  useEffect(() => {
    fetchEmails();
    if (isAdmin) api.get('/users').then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const fetchEmails = async (refresh = false) => {
    try {
      if (refresh) setRefreshing(true);
      const res = await api.get(`/gmail/emails${refresh ? '?refresh=true' : ''}`);
      setEmails(res.data.emails || []);
    } catch { toast.error('Errore caricamento email'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const openThread = async (email) => {
    setSelectedEmail(email);
    try {
      const [detailRes, repliesRes] = await Promise.all([
        api.get(`/gmail/email/${email.id}`),
        api.get(`/gmail/email/${email.id}/replies`)
      ]);
      setEmailDetail(detailRes.data);
      setReplies(repliesRes.data || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Errore caricamento dettaglio'); }
  };

  const downloadAttachment = async (emailId, partIndex, filename) => {
    try {
      const res = await api.get(`/gmail/email/${emailId}/attachment/${partIndex}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'allegato';
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); a.remove();
    } catch { toast.error('Errore download allegato'); }
  };

  const handleAssign = async (emailId, userId) => {
    try {
      await api.post(`/gmail/assign?email_id=${emailId}&user_id=${userId}`);
      toast.success('Email assegnata');
    } catch { toast.error('Errore assegnazione'); }
  };

  // --- Compose functions ---
  const openCompose = (mode = 'new', email = null) => {
    setComposeMode(mode);
    setComposeFiles([]);
    setShowCcBcc(false);
    setReplyToId('');

    if (mode === 'new') {
      setComposeTo(''); setComposeCc(''); setComposeBcc('');
      setComposeSubject(''); setComposeBody('');
    } else if (mode === 'reply' && email) {
      const fromEmail = extractEmail(emailDetail?.from || email.from);
      setComposeTo(fromEmail);
      setComposeCc(''); setComposeBcc('');
      setComposeSubject(`Re: ${email.subject || ''}`);
      setComposeBody(`\n\n--- Messaggio originale ---\nDa: ${getDisplayName(email.from)}\nData: ${formatDate(email.date)}\n\n${emailDetail?.body_full || emailDetail?.body || email.snippet || ''}`);
      setReplyToId(String(email.id));
    } else if (mode === 'replyAll' && email) {
      const fromEmail = extractEmail(emailDetail?.from || email.from);
      const toEmails = (emailDetail?.to || '').split(',').map(e => extractEmail(e)).filter(e => e && e !== fromEmail);
      setComposeTo(fromEmail);
      setComposeCc(toEmails.join(', ')); setComposeBcc('');
      setShowCcBcc(toEmails.length > 0);
      setComposeSubject(`Re: ${email.subject || ''}`);
      setComposeBody(`\n\n--- Messaggio originale ---\nDa: ${getDisplayName(email.from)}\nData: ${formatDate(email.date)}\n\n${emailDetail?.body_full || emailDetail?.body || email.snippet || ''}`);
      setReplyToId(String(email.id));
    } else if (mode === 'forward' && email) {
      setComposeTo(''); setComposeCc(''); setComposeBcc('');
      setComposeSubject(`Fwd: ${email.subject || ''}`);
      setComposeBody(`\n\n--- Messaggio inoltrato ---\nDa: ${getDisplayName(email.from)}\nData: ${formatDate(email.date)}\nOggetto: ${email.subject}\n\n${emailDetail?.body_full || emailDetail?.body || email.snippet || ''}`);
    }
    setComposeOpen(true);
  };

  const handleSendCompose = async () => {
    if (!composeTo.trim()) { toast.error('Inserisci almeno un destinatario'); return; }
    setSending(true);
    try {
      const cc = [composeCc, composeBcc].filter(Boolean).join(', ');
      if (composeFiles.length > 0) {
        const fd = new FormData();
        fd.append('to', composeTo);
        fd.append('subject', composeSubject);
        fd.append('body', composeBody);
        fd.append('cc', cc);
        fd.append('reply_to_id', replyToId);
        composeFiles.forEach(f => fd.append('files', f));
        await api.post('/gmail/send-with-attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post(`/gmail/send?to=${encodeURIComponent(composeTo)}&subject=${encodeURIComponent(composeSubject)}&body=${encodeURIComponent(composeBody)}&cc=${encodeURIComponent(cc)}&reply_to_id=${encodeURIComponent(replyToId)}`);
      }
      toast.success('Email inviata!');
      setComposeOpen(false);
      // Refresh replies if in thread
      if (selectedEmail && replyToId) {
        const repliesRes = await api.get(`/gmail/email/${selectedEmail.id}/replies`);
        setReplies(repliesRes.data || []);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch { toast.error('Errore invio email'); }
    finally { setSending(false); }
  };

  const filtered = emails.filter(e => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (e.subject || '').toLowerCase().includes(q) ||
      (e.from || '').toLowerCase().includes(q) ||
      (e.snippet || '').toLowerCase().includes(q);
  });

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="h-12 w-12 border-b-2 border-blue-600 rounded-full animate-spin" /></div>;

  // Quick reply handler - must be before thread view return
  const handleQuickReply = async (text, files, email) => {
    if (!text.trim() && files.length === 0) return;
    setSending(true);
    try {
      const toEmail = extractEmail(emailDetail?.from || email.from);
      const subject = `Re: ${email.subject || ''}`;
      if (files.length > 0) {
        const fd = new FormData();
        fd.append('to', toEmail); fd.append('subject', subject);
        fd.append('body', text); fd.append('reply_to_id', String(email.id));
        files.forEach(f => fd.append('files', f));
        await api.post('/gmail/send-with-attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post(`/gmail/send?to=${encodeURIComponent(toEmail)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}&reply_to_id=${encodeURIComponent(String(email.id))}`);
      }
      toast.success('Risposta inviata!');
      const repliesRes = await api.get(`/gmail/email/${email.id}/replies`);
      setReplies(repliesRes.data || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Errore invio risposta'); }
    finally { setSending(false); }
  };

  // ===== Thread view =====
  if (selectedEmail) {
    const allMessages = [];
    allMessages.push({
      type: 'incoming',
      from: getDisplayName(emailDetail?.from || selectedEmail.from),
      fromFull: emailDetail?.from || selectedEmail.from,
      body: emailDetail?.body_full || emailDetail?.body || selectedEmail.snippet || '',
      htmlBody: emailDetail?.html_body || null,
      date: emailDetail?.date || selectedEmail.date,
      attachments: emailDetail?.attachments || [],
      inlineImages: emailDetail?.inline_images || []
    });
    replies.forEach(r => {
      allMessages.push({
        type: 'outgoing', from: r.user_name || 'Team', internalUser: r.user_name,
        body: r.body_full || r.body || '', date: r.sent_at, attachmentCount: r.attachment_count || 0
      });
    });

    return (
      <div className="flex flex-col h-[calc(100vh-80px)]" data-testid="email-thread-view">
        {/* Thread header */}
        <div className="bg-white border-b px-5 py-3 flex items-center gap-3 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedEmail(null); setEmailDetail(null); setReplies([]); }} data-testid="back-to-inbox">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-slate-900 truncate text-sm">{selectedEmail.subject || 'Nessun oggetto'}</h2>
            <p className="text-xs text-slate-500">{getDisplayName(selectedEmail.from)}</p>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500" onClick={() => openCompose('reply', selectedEmail)} title="Rispondi" data-testid="reply-btn"><Reply size={15} /></Button>
            <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500" onClick={() => openCompose('replyAll', selectedEmail)} title="Rispondi a tutti" data-testid="reply-all-btn"><ReplyAll size={15} /></Button>
            <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500" onClick={() => openCompose('forward', selectedEmail)} title="Inoltra" data-testid="forward-btn"><Forward size={15} /></Button>
          </div>
          {/* Assegna a me - visibile a tutti */}
          {selectedEmail && (
            <Button size="sm" variant="outline" className="h-8 text-xs font-medium text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              onClick={() => handleAssign(String(selectedEmail.id), user?.id)} data-testid="assign-to-me-btn">
              <User size={13} className="mr-1" /> Assegna a me
            </Button>
          )}
          {isAdmin && (
            <Select onValueChange={(v) => handleAssign(String(selectedEmail.id), v)}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="assign-email-select">
                <User size={12} className="mr-1" /> <SelectValue placeholder="Assegna a..." />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Chat messages */}
        <ScrollArea className="flex-1 bg-slate-50">
          <div className="max-w-3xl mx-auto p-5 space-y-4">
            {allMessages.map((msg, i) => (
              <div key={i} className={`flex gap-3 ${msg.type === 'outgoing' ? 'justify-end' : ''}`} data-testid={`msg-${i}`}>
                {msg.type === 'incoming' && (
                  <Avatar className="h-8 w-8 flex-shrink-0"><AvatarFallback className="bg-blue-100 text-blue-700 text-xs">{getInitials(msg.from)}</AvatarFallback></Avatar>
                )}
                <div className={`max-w-[75%] ${msg.type === 'outgoing' ? 'items-end' : 'items-start'}`}>
                  <div className={`rounded-2xl px-4 py-3 ${msg.type === 'outgoing' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 rounded-bl-sm'}`}>
                    <div className={`flex items-center gap-2 mb-1 ${msg.type === 'outgoing' ? 'justify-end' : ''}`}>
                      <span className={`text-[10px] font-semibold ${msg.type === 'outgoing' ? 'text-blue-100' : 'text-slate-500'}`}>
                        {msg.from}{msg.type === 'outgoing' && msg.internalUser && <span className="ml-1 text-blue-200">(interno)</span>}
                      </span>
                    </div>
                    <div className={`text-sm whitespace-pre-wrap leading-relaxed ${msg.type === 'outgoing' ? 'text-white' : 'text-slate-700'}`}>
                      {msg.htmlBody ? (
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.htmlBody, { FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'], FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'] }) }} className="email-html-content [&_img]:max-w-full [&_img]:rounded [&_a]:text-blue-500 [&_a]:underline" />
                      ) : msg.body}
                    </div>
                  </div>
                  {msg.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {msg.attachments.map((att, ai) => {
                        const isImage = att.mimetype?.startsWith('image/');
                        return (
                          <div key={ai} className="space-y-1">
                            {isImage && att.data && (
                              <img src={`data:${att.mimetype};base64,${att.data}`} alt={att.filename || 'immagine'}
                                className="max-w-[280px] rounded-lg border border-slate-200 cursor-pointer hover:opacity-90"
                                onClick={() => {
                                  const w = window.open();
                                  if (!w) return;
                                  const img = w.document.createElement('img');
                                  img.src = `data:${att.mimetype};base64,${att.data}`;
                                  img.alt = att.filename || 'immagine';
                                  img.style.maxWidth = '100%';
                                  w.document.body.appendChild(img);
                                }} />
                            )}
                            <button onClick={() => downloadAttachment(selectedEmail.id, att.part_index ?? ai, att.filename)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 hover:border-blue-300 transition-colors cursor-pointer" data-testid={`attachment-${ai}`}>
                              <Paperclip size={12} className="text-slate-400" />
                              {att.filename || `Allegato ${ai + 1}`}
                              {att.size > 0 && <span className="text-slate-400">({(att.size / 1024).toFixed(0)}KB)</span>}
                              <Download size={11} className="text-blue-500" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {msg.inlineImages?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {msg.inlineImages.map((img, ii) => (
                        <img key={ii} src={`data:${img.mimetype};base64,${img.data}`} alt={img.filename || 'immagine'}
                          className="max-w-[280px] rounded-lg border border-slate-200" />
                      ))}
                    </div>
                  )}
                  <p className={`text-[10px] mt-1 ${msg.type === 'outgoing' ? 'text-right text-slate-400' : 'text-slate-400'}`}>{formatDate(msg.date)}</p>
                </div>
                {msg.type === 'outgoing' && (
                  <Avatar className="h-8 w-8 flex-shrink-0"><AvatarFallback className="bg-blue-600 text-white text-xs">{msg.internalUser ? getInitials(msg.internalUser) : 'FS'}</AvatarFallback></Avatar>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Quick reply bar */}
        <QuickReply
          onSend={(text, files) => {
            setComposeTo(extractEmail(emailDetail?.from || selectedEmail.from));
            setComposeSubject(`Re: ${selectedEmail.subject || ''}`);
            setComposeBody(text);
            setComposeFiles(files);
            setReplyToId(String(selectedEmail.id));
            setComposeMode('reply');
            handleQuickReply(text, files, selectedEmail);
          }}
        />

        {/* Compose dialog */}
        <ComposeDialog
          open={composeOpen} onOpenChange={setComposeOpen}
          mode={composeMode} to={composeTo} setTo={setComposeTo}
          cc={composeCc} setCc={setComposeCc} bcc={composeBcc} setBcc={setComposeBcc}
          subject={composeSubject} setSubject={setComposeSubject}
          body={composeBody} setBody={setComposeBody}
          files={composeFiles} setFiles={setComposeFiles}
          showCcBcc={showCcBcc} setShowCcBcc={setShowCcBcc}
          sending={sending} onSend={handleSendCompose}
          composeFileRef={composeFileRef}
        />
      </div>
    );
  }

  // ===== Inbox view =====
  return (
    <div className="p-4 sm:p-5 md:p-7" data-testid="emails-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Email</h1>
          <p className="text-xs sm:text-sm text-slate-500">Inbox condivisa del team</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => openCompose('new')} data-testid="compose-new-btn">
            <PenSquare size={14} className="mr-1.5" /> Nuova Email
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchEmails(true)} disabled={refreshing} data-testid="refresh-emails-btn">
            <RefreshCw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Aggiorna
          </Button>
        </div>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Cerca email..." className="pl-9 h-9 text-sm" data-testid="search-emails" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Inbox size={48} className="mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{searchQ ? 'Nessun risultato' : 'Nessuna email'}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((email, i) => (
              <button key={email.id || i} onClick={() => openThread(email)}
                className={`w-full text-left px-5 py-3.5 hover:bg-slate-50 transition-colors flex items-center gap-3 ${email.unread ? 'bg-blue-50/50' : ''}`}
                data-testid={`email-row-${i}`}>
                <Avatar className="h-9 w-9 flex-shrink-0">
                  <AvatarFallback className={`text-xs ${email.unread ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{getInitials(email.from)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {email.unread && <div className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />}
                    <p className={`text-sm truncate ${email.unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>{getDisplayName(email.from)}</p>
                    <span className="text-[10px] text-slate-400 flex-shrink-0 ml-auto">{formatDate(email.date)}</span>
                  </div>
                  <p className={`text-xs truncate ${email.unread ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>{email.subject || 'Nessun oggetto'}</p>
                  <p className="text-[11px] text-slate-400 truncate mt-0.5">{email.snippet || ''}</p>
                </div>
                {email.has_attachments && <Paperclip size={13} className="text-slate-400 flex-shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Compose dialog */}
      <ComposeDialog
        open={composeOpen} onOpenChange={setComposeOpen}
        mode={composeMode} to={composeTo} setTo={setComposeTo}
        cc={composeCc} setCc={setComposeCc} bcc={composeBcc} setBcc={setComposeBcc}
        subject={composeSubject} setSubject={setComposeSubject}
        body={composeBody} setBody={setComposeBody}
        files={composeFiles} setFiles={setComposeFiles}
        showCcBcc={showCcBcc} setShowCcBcc={setShowCcBcc}
        sending={sending} onSend={handleSendCompose}
        composeFileRef={composeFileRef}
      />
    </div>
  );
};

// --- Quick Reply Component ---
const QuickReply = ({ onSend }) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const handleSend = () => {
    if (!text.trim() && files.length === 0) return;
    onSend(text, files);
    setText(''); setFiles([]);
  };

  return (
    <div className="bg-white border-t px-5 py-3 flex-shrink-0">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {files.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
              <Paperclip size={10} /> {f.name}
              <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2 items-end">
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { setFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }} />
        <Button variant="ghost" size="sm" className="h-9 px-2 text-slate-400" onClick={() => fileRef.current?.click()} data-testid="attach-file-btn"><Paperclip size={18} /></Button>
        <Textarea value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Rispondi rapidamente..." className="flex-1 min-h-[40px] max-h-[120px] resize-none text-sm" rows={1} data-testid="reply-input" />
        <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700" onClick={handleSend} disabled={!text.trim() && files.length === 0} data-testid="send-reply-btn"><Send size={16} /></Button>
      </div>
    </div>
  );
};

// --- Compose Dialog Component ---
const ComposeDialog = ({ open, onOpenChange, mode, to, setTo, cc, setCc, bcc, setBcc, subject, setSubject, body, setBody, files, setFiles, showCcBcc, setShowCcBcc, sending, onSend, composeFileRef }) => {
  const modeLabels = { new: 'Nuova Email', reply: 'Rispondi', replyAll: 'Rispondi a tutti', forward: 'Inoltra' };
  const ModeIcon = { new: PenSquare, reply: Reply, replyAll: ReplyAll, forward: Forward }[mode] || PenSquare;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ModeIcon size={18} />{modeLabels[mode]}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <label className="text-[10px] font-semibold text-slate-500 mb-1 block">A *</label>
            <Input value={to} onChange={e => setTo(e.target.value)} placeholder="destinatario@email.com" className="h-9 text-sm" data-testid="compose-to" />
          </div>

          <button onClick={() => setShowCcBcc(!showCcBcc)} className="text-[11px] text-blue-600 hover:underline flex items-center gap-1" data-testid="toggle-cc-bcc">
            {showCcBcc ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {showCcBcc ? 'Nascondi' : 'Mostra'} CC / CCN
          </button>

          {showCcBcc && (
            <>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 mb-1 block">CC</label>
                <Input value={cc} onChange={e => setCc(e.target.value)} placeholder="copia@email.com, altro@email.com" className="h-9 text-sm" data-testid="compose-cc" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 mb-1 block">CCN (Copia nascosta)</label>
                <Input value={bcc} onChange={e => setBcc(e.target.value)} placeholder="nascosto@email.com" className="h-9 text-sm" data-testid="compose-bcc" />
              </div>
            </>
          )}

          <div>
            <label className="text-[10px] font-semibold text-slate-500 mb-1 block">Oggetto</label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Oggetto dell'email" className="h-9 text-sm" data-testid="compose-subject" />
          </div>

          <div>
            <label className="text-[10px] font-semibold text-slate-500 mb-1 block">Messaggio</label>
            <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Scrivi il messaggio..." rows={10} className="text-sm min-h-[200px]" data-testid="compose-body" />
          </div>

          {/* Attachments */}
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-lg text-xs">
                  <Paperclip size={11} className="text-slate-400" /> {f.name}
                  <span className="text-slate-400">({(f.size / 1024).toFixed(0)}KB)</span>
                  <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X size={11} /></button>
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <div>
              <input ref={composeFileRef} type="file" multiple className="hidden" onChange={(e) => { setFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }} />
              <Button variant="outline" size="sm" onClick={() => composeFileRef.current?.click()} data-testid="compose-attach-btn">
                <Paperclip size={14} className="mr-1.5" /> Allega
              </Button>
            </div>
            <Button onClick={onSend} disabled={sending || !to.trim()} data-testid="compose-send-btn">
              {sending ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
              Invia
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
